import { useState, useEffect } from "react"
import { Loader2, DollarSign, Users, Building2, Calendar, TrendingUp } from "lucide-react"
import AdminSidebar from "../components/AdminSidebar"
import { useLanguage } from "../lib/LanguageContext"

// Dashboard data lives on Vietnam-App's backend (shared database); auth
// itself still runs on this app's own :8001.
const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function AdminDashboard() {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      try {
        const res = await fetch(`${API}/api/admin/dashboard`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch dashboard data")
        const json = await res.json()
        // The backend doesn't track revenue or compute leaderboards yet
        // (no payments feature built out) — default those fields so the
        // page renders a real empty state instead of crashing on them.
        setData({
          total_revenue: 0,
          pending_payment: 0,
          revenue_7d: [0, 0, 0, 0, 0, 0, 0],
          top_clubs: [],
          biggest_events: [],
          ...json,
        })
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <AdminSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("admin_dash_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">
              {t("admin_dash_subtitle")}
            </p>
          </div>
        </header>

        {error && (
          <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={<Users className="text-blue-400" />} label={t("admin_dash_verified_swimmers")} value={data.total_swimmers} />
              <StatCard icon={<Building2 className="text-green-400" />} label={t("admin_dash_active_clubs")} value={data.total_clubs} sub={`+ ${data.pending_clubs} ${t("admin_dash_pending")}`} />
              <StatCard icon={<Calendar className="text-purple-400" />} label={t("admin_dash_total_events")} value={data.total_events} />
              <StatCard icon={<DollarSign className="text-gold-400" />} label={t("admin_dash_total_revenue")} value={`$${data.total_revenue}`} sub={`$${data.pending_payment} ${t("admin_dash_pending")}`} />
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Financial Sparkline Placeholder */}
              <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm">
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-mist-400 mb-4">
                  <TrendingUp className="h-4 w-4" /> {t("admin_dash_7d_revenue")}
                </h3>
                <div className="flex h-40 items-end gap-2">
                  {data.revenue_7d.map((val, i) => {
                    const max = Math.max(...data.revenue_7d, 1) // prevent div by zero
                    const height = `${(val / max) * 100}%`
                    return (
                      <div key={i} className="group relative flex flex-1 items-end justify-center">
                        <div style={{ height }} className="w-full rounded-t-sm bg-gold-500/50 transition-all group-hover:bg-gold-400"></div>
                        <span className="absolute -top-6 hidden rounded bg-ink-900 px-2 py-1 text-xs text-white group-hover:block">${val}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Leaderboards */}
              <div className="space-y-8">
                {/* Top Clubs */}
                <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm">
                  <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-mist-400">{t("admin_dash_top_clubs")}</h3>
                  <div className="divide-y divide-white/5">
                    {data.top_clubs.map((c, i) => (
                      <div key={c.id} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-mist-500 font-bold w-4">{i + 1}</span>
                          <span className="font-medium text-paper-0">{c.name}</span>
                        </div>
                        <span className="text-sm text-mist-300">{c.members} {t("admin_dash_members")}</span>
                      </div>
                    ))}
                    {data.top_clubs.length === 0 && <p className="py-4 text-sm text-mist-500 text-center">{t("admin_dash_no_clubs")}</p>}
                  </div>
                </div>

                {/* Biggest Events */}
                <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm">
                  <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-mist-400">{t("admin_dash_biggest_events")}</h3>
                  <div className="divide-y divide-white/5">
                    {data.biggest_events.map((e, i) => (
                      <div key={e.id} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-mist-500 font-bold w-4">{i + 1}</span>
                          <span className="font-medium text-paper-0">{e.title}</span>
                          <span className="text-[10px] uppercase bg-white/10 px-2 py-0.5 rounded text-mist-300">{e.status}</span>
                        </div>
                        <span className="text-sm text-mist-300">{e.paid_count} {t("admin_dash_registrations")}</span>
                      </div>
                    ))}
                    {data.biggest_events.length === 0 && <p className="py-4 text-sm text-mist-500 text-center">{t("admin_dash_no_regs")}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  )
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="flex flex-col justify-center rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/5">
        {icon}
      </div>
      <p className="text-sm font-semibold uppercase tracking-wider text-mist-400">{label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <p className="font-display text-3xl font-bold text-paper-0">{value}</p>
        {sub && <p className="text-xs font-medium text-mist-500">{sub}</p>}
      </div>
    </div>
  )
}
